import { KeyboardKey } from "assets/js/enums/keyboard.enum";
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { Button, CalendarCell, CalendarGrid, CalendarGridBody, CalendarGridHeader, CalendarHeaderCell, RangeCalendar } from "react-aria-components";
import { parseDate, type CalendarDate } from "@internationalized/date";
import "./dateTimeRangeCalendar.css";
import { FocusScope, I18nProvider, useDateFormatter } from "react-aria";
import { _, _any, type Locale } from "i18n";
import { ArrowLeft, ArrowRight } from "ui-kit/src/icons";

interface BaseRangeCalendarProps {
	start: string;
	end: string;
	startTime?: string;
	endTime?: string;
	onChange: (start: string, end: string) => void;
	placeholder?: string;
	apiFormat?: boolean;
	iconArrow?: boolean;
}

interface DateRangeCalendarProps extends BaseRangeCalendarProps {
	locale: Locale;
	placeholder?: string;
}

interface RangeValue {
	start: CalendarDate;
	end: CalendarDate;
}

function parseDateTime(dateTimeStr: string) {
	const [dateStr, timeStrWithTz] = dateTimeStr.split("T");
	const date = parseDate(dateStr);
	const [timeStr] = timeStrWithTz ? timeStrWithTz.split("Z") : ["00:00:00"];
	return { date, time: timeStr };
}

function ensureTimeFormat(time: string) {
	const [hours, minutes, seconds] = time.split(":");
	return `${hours || "00"}:${minutes || "00"}:${seconds || "00"}`;
}

const CALENDAR_VIEWPORT_PADDING = 8;
const CALENDAR_TRIGGER_GAP = 4;

interface CalendarOverlayPosition {
	top: number;
	left: number;
	maxHeight?: number;
}

function parseCssPixelLength(value: string) {
	const parsedValue = Number.parseFloat(value);
	if (Number.isFinite(parsedValue) === false) {
		return 0;
	}

	return parsedValue;
}

function getCalendarOverlayContentHeight(
	calendarElement: HTMLDivElement,
	calendarBodyElement: HTMLDivElement | null,
	calendarFooterElement: HTMLDivElement | null,
) {
	if (calendarBodyElement === null || calendarFooterElement === null) {
		return calendarElement.scrollHeight;
	}

	const calendarStyle = window.getComputedStyle(calendarElement);
	const verticalChrome =
		parseCssPixelLength(calendarStyle.paddingTop) +
		parseCssPixelLength(calendarStyle.paddingBottom) +
		parseCssPixelLength(calendarStyle.borderTopWidth) +
		parseCssPixelLength(calendarStyle.borderBottomWidth);

	return verticalChrome + calendarBodyElement.scrollHeight + calendarFooterElement.offsetHeight;
}

function getConstrainedCalendarOverlayPosition(
	triggerBox: DOMRect,
	calendarWidth: number,
	contentHeight: number,
): CalendarOverlayPosition {
	const viewportWidth = window.innerWidth;
	const viewportHeight = window.innerHeight;
	const maxViewportHeight = Math.max(0, viewportHeight - CALENDAR_VIEWPORT_PADDING * 2);

	let left = triggerBox.left;
	if (calendarWidth > 0) {
		const maxLeft = viewportWidth - calendarWidth - CALENDAR_VIEWPORT_PADDING;
		left = Math.min(left, Math.max(CALENDAR_VIEWPORT_PADDING, maxLeft));
	}

	const availableBelow = Math.max(0, viewportHeight - triggerBox.bottom - CALENDAR_TRIGGER_GAP - CALENDAR_VIEWPORT_PADDING);
	const availableAbove = Math.max(0, triggerBox.top - CALENDAR_TRIGGER_GAP - CALENDAR_VIEWPORT_PADDING);
	const fitsBelow = contentHeight > 0 && contentHeight <= availableBelow;
	const fitsAbove = contentHeight > 0 && contentHeight <= availableAbove;
	const placeAbove = fitsBelow === false && (fitsAbove || availableAbove > availableBelow);

	let availableHeight = availableBelow;
	if (placeAbove) {
		availableHeight = availableAbove;
	}

	let maxHeight = availableHeight;
	if (contentHeight > 0) {
		maxHeight = Math.min(contentHeight, availableHeight);
	}

	if (maxHeight === 0) {
		maxHeight = maxViewportHeight;
	}

	maxHeight = Math.min(maxHeight, maxViewportHeight);

	let top = triggerBox.bottom + CALENDAR_TRIGGER_GAP;
	if (placeAbove) {
		top = triggerBox.top - maxHeight - CALENDAR_TRIGGER_GAP;
	}

	top = Math.max(CALENDAR_VIEWPORT_PADDING, Math.min(top, viewportHeight - CALENDAR_VIEWPORT_PADDING - maxHeight));

	return { top, left, maxHeight };
}

function BaseRangeTimeCalendar({ start, end, onChange, placeholder, apiFormat = true, iconArrow = false }: BaseRangeCalendarProps) {
	const dateFormatter = useDateFormatter({
		month: "long",
		year: "numeric",
	});

	const currentDate = new Date().toISOString().split("T")[0];
	const triggerRef = useRef<HTMLDivElement>(null);
	const calendarRef = useRef<HTMLDivElement>(null);
	const calendarBodyRef = useRef<HTMLDivElement>(null);
	const calendarFooterRef = useRef<HTMLDivElement>(null);
	const calendarDialogId = useId();
	const [show, setShow] = useState(false);
	const [calendarPosition, setCalendarPosition] = useState<CalendarOverlayPosition>({ top: 0, left: 0 });
	const calendarDialogLabel = placeholder === undefined || placeholder === "" ? _("filter.daterange.placeholder") : placeholder;

	const [range, setRange] = useState({
		start: parseDate(currentDate),
		end: parseDate(currentDate),
		startTime: "00:00:00",
		endTime: "23:59:59",
	});

	useEffect(() => {
		if (start && end) {
			const parsedStart = parseDateTime(start);
			const parsedEnd = parseDateTime(end);

			setRange({
				start: parsedStart.date,
				end: parsedEnd.date,
				startTime: ensureTimeFormat(parsedStart.time || "00:00:00"),
				endTime: ensureTimeFormat(parsedEnd.time || "23:59:59"),
			});
		} else {
			setRange({
				start: parseDate(currentDate),
				end: parseDate(currentDate),
				startTime: "00:00:00",
				endTime: "23:59:59",
			});
		}
	}, [start, end, currentDate]);

	useLayoutEffect(() => {
		if (show === false) {
			return;
		}

		const updateCalendarPosition = () => {
			if (triggerRef.current === null || calendarRef.current === null) {
				return;
			}

			const nextPosition = getConstrainedCalendarOverlayPosition(
				triggerRef.current.getBoundingClientRect(),
				calendarRef.current.offsetWidth,
				getCalendarOverlayContentHeight(calendarRef.current, calendarBodyRef.current, calendarFooterRef.current),
			);

			setCalendarPosition((currentPosition) => {
				if (
					currentPosition.top === nextPosition.top &&
					currentPosition.left === nextPosition.left &&
					currentPosition.maxHeight === nextPosition.maxHeight
				) {
					return currentPosition;
				}

				return nextPosition;
			});
		};

		updateCalendarPosition();
		calendarRef.current?.focus({ preventScroll: true });
		const frameId = requestAnimationFrame(updateCalendarPosition);
		window.addEventListener("scroll", updateCalendarPosition, true);
		window.addEventListener("resize", updateCalendarPosition);

		const resizeObserver = new ResizeObserver(updateCalendarPosition);
		const calendarElement = calendarRef.current;
		const calendarBodyElement = calendarBodyRef.current;
		if (calendarElement !== null) {
			resizeObserver.observe(calendarElement);
		}
		if (calendarBodyElement !== null) {
			resizeObserver.observe(calendarBodyElement);
		}

		return () => {
			cancelAnimationFrame(frameId);
			window.removeEventListener("scroll", updateCalendarPosition, true);
			window.removeEventListener("resize", updateCalendarPosition);
			resizeObserver.disconnect();
		};
	}, [show]);

	useEffect(() => {
		if (show === false) {
			return;
		}

		const closeOnOutsidePointer = (event: MouseEvent) => {
			const target = event.target;
			if ((target instanceof Node) === false) {
				return;
			}

			if (triggerRef.current?.contains(target)) {
				return;
			}

			if (calendarRef.current?.contains(target)) {
				return;
			}

			setShow(false);
		};

		const closeOnEscape = (event: globalThis.KeyboardEvent) => {
			if (event.key === KeyboardKey.Escape) {
				event.preventDefault();
				event.stopPropagation();
				setShow(false);
			}
		};

		document.addEventListener("mousedown", closeOnOutsidePointer);
		document.addEventListener("keydown", closeOnEscape);
		return () => {
			document.removeEventListener("mousedown", closeOnOutsidePointer);
			document.removeEventListener("keydown", closeOnEscape);
		};
	}, [show]);

	const dayStrings: Record<string, string> = {
		mon: "datepicker.days_short.monday",
		tue: "datepicker.days_short.tuesday",
		wed: "datepicker.days_short.wednesday",
		thu: "datepicker.days_short.thursday",
		fri: "datepicker.days_short.friday",
		sat: "datepicker.days_short.saturday",
		sun: "datepicker.days_short.sunday",
	};

	const getDayString = (day: string) => {
		return dayStrings[day.toLowerCase()] || day;
	};

	const formatDateTime = (date: CalendarDate, time: string) => {
		return time
			? `${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}T${ensureTimeFormat(time)}Z`
			: `${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`;
	};

	const formatOutputDateTime = (date: CalendarDate, time: string) => {
		const formattedTime = ensureTimeFormat(time || (date === range.start ? "00:00:00" : "23:59:59"));
		return apiFormat
			? `${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}T${formattedTime}Z`
			: formatDateTime(date, formattedTime);
	};

	const handleApply = () => {
		const formattedStart = formatOutputDateTime(range.start, range.startTime);
		const formattedEnd = formatOutputDateTime(range.end, range.endTime);
		onChange(formattedStart, formattedEnd);
		setShow(false);
	};

	const handleCancel = () => {
		onChange("", "");
		setRange({
			start: parseDate(currentDate),
			end: parseDate(currentDate),
			startTime: "00:00:00",
			endTime: "23:59:59",
		});
		setShow(false);
	};

	const handleTriggerKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === KeyboardKey.Enter) {
			event.preventDefault();
			setShow((isOpen) => isOpen === false);
			return;
		}

		// Prevent page scroll. Space opens on keyup so a still-held Space cannot
		// activate a React Aria Button after focus moves into the overlay.
		if (event.key === KeyboardKey.Space) {
			event.preventDefault();
		}
	};

	const handleTriggerKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === KeyboardKey.Space) {
			event.preventDefault();
			setShow((isOpen) => isOpen === false);
		}
	};

	const handleTimeChange = (type: "startTime" | "endTime", time: string) => {
		setRange((prevRange) => ({
			...prevRange,
			[type]: ensureTimeFormat(time),
		}));
	};

	const handleDateChange = (value: RangeValue) => {
		setRange((prevRange) => ({
			...prevRange,
			start: value.start,
			end: value.end,
		}));
	};

	function formatDateTimeForDisplay(date: CalendarDate, time: string) {
		const formattedDate = `${String(date.day).padStart(2, "0")}/${String(date.month).padStart(2, "0")}/${date.year}`;
		const formattedTime = ensureTimeFormat(time);
		return `${formattedDate} ${formattedTime}`;
	}

	const calendarPanel = (
		<div className="dateTimeRangeCalendar-overlayRoot">
		<FocusScope contain restoreFocus>
		<div
			id={calendarDialogId}
			ref={calendarRef}
			className="calendarContainer"
			role="dialog"
			aria-modal="true"
			aria-label={calendarDialogLabel}
			tabIndex={-1}
			style={{
				top: calendarPosition.top,
				left: calendarPosition.left,
				maxHeight: calendarPosition.maxHeight,
			}}
		>
			<div ref={calendarBodyRef} className="calendarBody">
				<RangeCalendar
					value={{ start: range.start, end: range.end }}
					onChange={handleDateChange}
					visibleDuration={{ months: 2 }}
					pageBehavior="single"
				>
					{({ state: { visibleRange, timeZone } }) => (
						<>
							<header className="calendarHeading">
								<Button slot="previous">
									<ArrowLeft />
								</Button>
								{dateFormatter.formatRange(visibleRange.start.toDate(timeZone), visibleRange.end.toDate(timeZone))}
								<Button slot="next">
									<ArrowRight />
								</Button>
							</header>
							<div className="calendarMonths">
								<CalendarGrid weekdayStyle="short">
									<CalendarGridHeader>
										{(day) => (
											<CalendarHeaderCell style={{ color: "var(--blue)" }}>{_any(getDayString(day))}</CalendarHeaderCell>
										)}
									</CalendarGridHeader>
									<CalendarGridBody>{(date) => <CalendarCell date={date} />}</CalendarGridBody>
								</CalendarGrid>
								<CalendarGrid weekdayStyle="short" offset={{ months: 1 }}>
									<CalendarGridHeader>
										{(day) => (
											<CalendarHeaderCell style={{ color: "var(--blue)" }}>{_any(getDayString(day))}</CalendarHeaderCell>
										)}
									</CalendarGridHeader>
									<CalendarGridBody>{(date) => <CalendarCell date={date} />}</CalendarGridBody>
								</CalendarGrid>
							</div>
						</>
					)}
				</RangeCalendar>
				<div className="timePickers">
					<label>
						Start Time:
						<input type="time" value={range.startTime} onChange={(e) => handleTimeChange("startTime", e.target.value)} step="1" />
					</label>
					<label>
						End Time:
						<input type="time" value={range.endTime} onChange={(e) => handleTimeChange("endTime", e.target.value)} step="1" />
					</label>
				</div>
			</div>
			<div ref={calendarFooterRef} className="calendarBottom">
				<span>{`${formatDateTimeForDisplay(range.start, range.startTime)} - ${formatDateTimeForDisplay(range.end, range.endTime)}`}</span>
				<Button className="calendarButton-cancel" onPress={handleCancel}>
					{_("btn.cancel")}
				</Button>
				<Button className="calendarButton-apply" onPress={handleApply}>
					{_("daterange.applyLabel")}
				</Button>
			</div>
		</div>
		</FocusScope>
		</div>
	);

	return (
		<div>
			<div
				ref={triggerRef}
				className="calendarInput-container"
				onClick={() => {
					setShow((isOpen) => isOpen === false);
				}}
			>
				{iconArrow && !(start && end) && <span className="icon-new icon-select-arrow-down" />}
				<input
					readOnly
					placeholder={placeholder}
					aria-expanded={show}
					aria-haspopup="dialog"
					aria-controls={show ? calendarDialogId : undefined}
					value={
						start && `${formatDateTimeForDisplay(range.start, range.startTime)} - ${formatDateTimeForDisplay(range.end, range.endTime)}}`
					}
					className={`calendarInput ${start && end ? "hasValue" : ""}`}
					onKeyDown={handleTriggerKeyDown}
					onKeyUp={handleTriggerKeyUp}
				/>
				{start && end && (
					<span
						onClick={(event) => {
							event.stopPropagation();
							handleCancel();
						}}
						className="icon-new icon-cross icon-white"
					/>
				)}
			</div>
			{show && createPortal(calendarPanel, document.body)}
		</div>
	);
}

export function DateTimeRangeCalendar({ placeholder, locale, start, end, onChange, ...props }: DateRangeCalendarProps) {
	return (
		<I18nProvider locale={locale}>
			<BaseRangeTimeCalendar placeholder={placeholder} start={start} end={end} onChange={onChange} {...props} />
		</I18nProvider>
	);
}
